<template>
  <div
    class="m-search-result-options"
  >
    <div class="flex-container">
      <div
        class="layout-options-container"
        v-if="showLayoutOptions"
      >
        <template
          v-for="layout in computedLayoutOptions"
          :key="layout"
        >
          <label
            class="_screenreader-readable"
            :for="`${layout}-button`"
          >
            {{ i18n[locale][layout].accessibilityLabel }}
          </label>
          <button
            :id="`${layout}-button`"
            :title="i18n[locale][layout].accessibilityLabel"
            :class="[
              'layout-option',
              'reset',
              {'-disabled': layoutIsDisabled(layout) },
              {'-active': !(layoutIsDisabled(layout)) && layout === computedCurrentLayout }
            ]"
            v-on="!( layoutIsDisabled(layout))? { click: () => changeLayout(layout) } : {}"
          >
            <i
              :class="[`icon-${layoutsIcons[layout]}`]"
              :title="i18n[locale][layout].accessibilityLabel"
            />
          </button>
        </template>
      </div>
      <div
        v-if="!shouldDisableComponent"
        class="result-count-container"
      >
        <slot
          name="result-count"
          v-bind="{ currentRange, total }"
        />
      </div>
    </div>
    <template v-if="showToggleFilters">
      <label
        class="_screenreader-readable"
        :for="toggle-filters-button"
      >
        {{ i18n[locale].toggle.accessibilityLabel }}
      </label>
      <button
        id="toggle-filters-button"
        :class="[
          'reset',
          'toggle-filters',
          {'-disabled': shouldDisableComponent },
          {'-active': !(shouldDisableComponent) && filtersOpened}
        ]"
        v-on="!shouldDisableComponent ? { click: toggleFilters } : {}"
      >
        <i
          class="icon-filter-o"
          :title="i18n[locale].toggle.accessibilityLabel"
        />
      </button>
    </template>
  </div>
</template>

<script>
import enTranslations from '../../../i18n/en_CA/layout-options.json'
import frTranslations from '../../../i18n/fr_CA/layout-options.json'

export default {
  name: 'SearchResultOptions',
  props: {
    locale: {
      type: String,
      default: 'en'
    },
    disabledLayouts: {
      type: [Boolean, Array],
      default: false
    },
    layoutOptions: {
      type: Array,
      default: () => ['detailed', 'card', 'compact'],
      validator (arr) {
        return arr.every(layout => ['detailed', 'card', 'compact'].includes(layout))
      }
    },
    useLayoutDefaultOrder: {
      type: Boolean,
      default: true
    },
    currentLayout: {
      type: String,
      default: undefined
    },
    total: {
      type: Number,
      required: true
    },
    currentPage: {
      type: Number,
      required: true,
      validator: val => val >= 1
    },
    nbPerPage: {
      type: Number,
      required: true,
      validator: val => val >= 1
    },
    showToggleFilters: {
      type: Boolean,
      default: false
    },
    filtersOpened: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      layoutsDefaultOrder: {
        card: 1,
        detailed: 2,
        compact: 3
      },
      layoutsIcons: {
        detailed: 'detailed-layout',
        card: 'card-layout',
        compact: 'u-list'
      },
      i18n: {
        fr: frTranslations,
        en: enTranslations
      }
    }
  },
  computed: {
    showLayoutOptions () {
      return this.layoutOptions.length > 1
    },
    currentRange () {
      if (this.total < 1 || this.currentPage < 1 || this.nbPerPage < 1) {
        return false
      }

      if (this.total < this.nbPerPage) {
        return false
      }

      const start = (this.currentPage - 1) * this.nbPerPage + 1

      if (start > this.total) {
        return false
      }

      let end = this.currentPage * this.nbPerPage
      if (end > this.total) {
        end = this.total
      }

      return `${start} - ${end}`
    },
    computedLayoutOptions () {
      return !this.useLayoutDefaultOrder ? this.layoutOptions : this.sortLayoutOptions()
    },
    computedDisabledLayouts () {
      if (!(Array.isArray(this.disabledLayouts))) {
        return this.disabledLayouts ? this.layoutOptions : []
      }
      return this.disabledLayouts
    },
    shouldDisableComponent () {
      return this.layoutOptions.every(layout => this.computedDisabledLayouts.includes(layout))
    },
    computedCurrentLayout () {
      if (!this.currentLayout || this.computedDisabledLayouts.includes(this.currentLayout)) {
        return this.computedLayoutOptions.find(layout => !this.computedDisabledLayouts.includes(layout))
      }
      return this.currentLayout
    }
  },
  methods: {
    changeLayout (layout) {
      this.$emit('changeLayout', layout)
    },
    toggleFilters () {
      this.$emit('toggleFilters')
    },
    sortLayoutOptions () {
      return [...this.layoutOptions].sort((a, b) =>
        this.layoutsDefaultOrder[a] > this.layoutsDefaultOrder[b])
    },
    layoutIsDisabled (layout) {
      return this.computedDisabledLayouts.includes(layout)
    }
  }
}
</script>

<style lang="sass">
@import '../../../sass/accessibility'
@import '../../../sass/variables'
@import '../../../sass/utilities'

.m-search-result-options
  display: flex
  align-items: center
  justify-content: space-between

  .layout-options-container
    color: $grey-500
    margin-right: $block-spacing
    display: flex

  button
    min-width: 20px
    line-height: 0
    &.layout-option
      margin-right: $block-spacing
      font-size: $size-4
      &.-active
        color: $black
    &.-disabled
      cursor: unset
      color: $grey-100
    &:hover:not(.-disabled)
      color: $link-color
      transition: color 0.2s
    &.reset.toggle-filters
      padding: calc($block-spacing/2)
      border-radius: 50%
      font-size: 1.2em
      display: flex
      &.-active
        transition: background-color 0.2s
        background-color: $link-color
        color: $white

    .icon-u-list
      font-size: 0.8em

  +until($smallscreen)
    .flex-container
      flex-direction: column
      align-items: flex-start
      .layout-options-container
        margin-top: calc($block-spacing/2)
        margin-bottom: $block-spacing
    .reset.toggle-filters
      align-self: flex-start
      margin-top: calc($block-spacing/8)
</style>
